import {
  NotificationSearchFilters,
  SearchFilterFieldName,
  SearchFilterOptionEnum,
  WorstSensorStatusOption,
} from '@components/cockpit/panels/notificationSearch/notificationSearch.types';
import { DEFAULT_PAGE_SIZE } from '@components/cockpit/panels/notificationSearch/NotificationSearchPanel';
import {
  DisplaySearchFilterOptionType,
  useDisplaySearchFilterOption,
} from '@components/cockpit/panels/notificationSearch/useDisplaySearchFilterOptions';
import {
  initialSearchFilterFieldState,
  SearchFilterFieldAction,
  searchFilterFieldReducer,
  SearchFilterFieldState,
} from '@components/cockpit/panels/notificationSearch/useSearchFilterField';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveGroupOrganization, selectActiveSite } from '@redux/authent/authent.selectors';
import {
  useGetDroneStatisticsQuery,
  useLazyGetNotificationsQuery,
  useTotalOfNotificationsQuery,
} from '@services/data/notification.api';
import { getDateTimeRangeFromPreset, toISOStringWithTimeZone } from '@utils/date.utils';
import {
  ActionDispatch,
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import {
  NotificationCriteria,
  NotificationFamilyEnum,
  NotificationStatusEnum,
  NotificationType,
  NotificationTypeEnum,
} from '@/types/data/data.types';
import { DroneStatistics } from '@/types/data/statistic.types';
import { ReliabilityEnum } from '@/types/sensor/identification.types';
import { ConnectorStatusEnum, HealthStatusEnum } from '@/types/sensor/sensors';

type NotificationSearchContextType = {
  isDirty: boolean;
  notificationsTotal: number | undefined;
  criteria: NotificationCriteria;
  isFetching: boolean;
  page: number;
  setPage: (page: number) => void;
  filters: NotificationSearchFilters;
  setFilters: ActionDispatch<[newFilters: Partial<NotificationSearchFilters>]>;
  resetFilters: (category?: NotificationFamilyEnum) => void;
  selectedNotification: string | null;
  setSelectedNotification: Dispatch<string | null>;
  notifications: NotificationType[];
  isOpenedExportDialog: boolean;
  setIsOpenedExportDialog: (open: boolean) => void;
  droneStatistics: DroneStatistics | undefined;
  isFetchingDroneStatistics: boolean;
  openedReporting: boolean;
  setOpenedReporting: (open: boolean) => void;
  openedFilterOptions: boolean;
  setOpenedFilterOptions: Dispatch<SetStateAction<boolean>>;
  activeSearchFilterField: SearchFilterFieldName | null;
  setActiveSearchFilterField: (panel: SearchFilterFieldName | null) => void;
  searchFilterFieldState: SearchFilterFieldState;
  setSearchFilterFieldState: ActionDispatch<[action: SearchFilterFieldAction]>;
  activeTab: NotificationFamilyEnum;
  setActiveTab: (panel: NotificationFamilyEnum) => void;
} & Omit<DisplaySearchFilterOptionType, 'toggleDisplayedFilterOptionCategory'>;

export const NotificationSearchContext = createContext<NotificationSearchContextType | null>(null);

export const NotificationSearchContextProvider = ({ children }: Readonly<PropsWithChildren>) => {
  const activeSite = useAppSelector(selectActiveSite);
  const organization = useAppSelector(selectActiveGroupOrganization);

  const [openedFilterOptions, setOpenedFilterOptions] = useState(false);
  const [openedReporting, setOpenedReporting] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isOpenedExportDialog, setIsOpenedExportDialog] = useState<boolean>(false);
  const [activeSearchFilterField, setActiveSearchFilterField] = useState<SearchFilterFieldName | null>(null);
  const [searchFilterFieldState, setSearchFilterFieldState] = useReducer(
    searchFilterFieldReducer,
    initialSearchFilterFieldState,
  );
  const [activeTab, setActiveTab] = useState<NotificationFamilyEnum>(NotificationFamilyEnum.LAD);

  const getDefaultFilters = useCallback(
    (category = NotificationFamilyEnum.LAD): NotificationSearchFilters => ({
      category,
      identifiers: [],
      displayIds: [],
      range: getDateTimeRangeFromPreset('ROLLING_MONTH'),
      sort: null,
      worstClassification: new Set([]),
      useAltitude: false,
      minimumMaxHeight: 0,
      maximumMaxHeight: 10000,
      reliability: ReliabilityEnum.SURE,
      duration:
        organization?.organizationParameter.enableProcedureAutoArchive && category !== NotificationFamilyEnum.LAD
          ? organization.organizationParameter.expiredNotificationsTtl * 60
          : 5,
      trackSensorUniqueKeys: [],
      zoneIds: [],
      worstSensorStatus: new Set<WorstSensorStatusOption>([
        HealthStatusEnum.DEGRADED,
        HealthStatusEnum.KO,
        ConnectorStatusEnum.UNREACHABLE,
      ]),
      platforms: [],
    }),
    [
      organization?.organizationParameter.enableProcedureAutoArchive,
      organization?.organizationParameter.expiredNotificationsTtl,
    ],
  );

  const [filters, setFilters] = useReducer(
    (filters: NotificationSearchFilters, newFilters: Partial<NotificationSearchFilters>) => ({
      ...filters,
      ...newFilters,
    }),
    getDefaultFilters(),
  );

  const { displayedFilterOptions, toggleDisplayedFilterOptionCategory, ...restDisplaySearchFilterOption } =
    useDisplaySearchFilterOption(filters.category);
  const criteria: NotificationCriteria = useMemo(() => {
    const commonCriteria = {
      site: activeSite!.code,
      page,
      size: DEFAULT_PAGE_SIZE,
      from: toISOStringWithTimeZone(filters.range.from),
      to: toISOStringWithTimeZone(filters.range.to),
      notificationStatus: NotificationStatusEnum.TERMINATED,
      platforms: filters.platforms,
    };
    switch (filters.category) {
      case NotificationFamilyEnum.LAD: {
        return {
          ...commonCriteria,
          types: [NotificationTypeEnum.DRONE],
          ...(displayedFilterOptions.has(SearchFilterOptionEnum.COLLABORATIVE_ID) && {
            identifiers: filters.identifiers,
            displayIds: filters.displayIds,
          }),
          ...(displayedFilterOptions.has(SearchFilterOptionEnum.WORST_CLASSIFICATION) && {
            worstClassification: Array.from(filters.worstClassification),
          }),
          ...(displayedFilterOptions.has(SearchFilterOptionEnum.MAX_ALTITUDE_OR_HEIGHT) && {
            minimumMaxHeight: filters.minimumMaxHeight,
            maximumMaxHeight: filters.maximumMaxHeight,
          }),
          ...(displayedFilterOptions.has(SearchFilterOptionEnum.RELIABILITY) && { reliability: filters.reliability }),
          ...(displayedFilterOptions.has(SearchFilterOptionEnum.DETECTION_DURATION) && { duration: filters.duration }),
          ...(displayedFilterOptions.has(SearchFilterOptionEnum.TRACK_SENSOR) && {
            trackSensorUniqueKeys: filters.trackSensorUniqueKeys,
          }),
          ...(displayedFilterOptions.has(SearchFilterOptionEnum.ZONES) && { zoneIds: filters.zoneIds }),
        };
      }
      case NotificationFamilyEnum.PERIMETER:
        return {
          ...commonCriteria,
          identifiers: filters.identifiers,
          types: [NotificationTypeEnum.SEGMENT],
        };
      case NotificationFamilyEnum.TECHNICAL:
        return {
          ...commonCriteria,
          types: [NotificationTypeEnum.SENSOR],
          ...(displayedFilterOptions.has(SearchFilterOptionEnum.ID) && {
            identifiers: filters.identifiers,
          }),
          ...(displayedFilterOptions.has(SearchFilterOptionEnum.WORST_SENSOR_STATUS) && {
            worstSensorStatus: Array.from(filters.worstSensorStatus),
          }),
          ...(displayedFilterOptions.has(SearchFilterOptionEnum.TREATMENT_DURATION) && { duration: filters.duration }),
        };
    }
  }, [
    activeSite,
    page,
    filters.range.from,
    filters.range.to,
    filters.platforms,
    filters.category,
    filters.identifiers,
    filters.worstSensorStatus,
    filters.duration,
    filters.displayIds,
    filters.worstClassification,
    filters.minimumMaxHeight,
    filters.maximumMaxHeight,
    filters.reliability,
    filters.trackSensorUniqueKeys,
    filters.zoneIds,
    displayedFilterOptions,
  ]);

  const { data: notificationsTotal } = useTotalOfNotificationsQuery(criteria);
  const { data: droneStatistics, isFetching: isFetchingDroneStatistics } = useGetDroneStatisticsQuery(criteria);

  const [triggerCallGetNotifications, { isFetching }] = useLazyGetNotificationsQuery();

  useEffect(() => {
    triggerCallGetNotifications(criteria).then((result) => {
      setNotifications(result.data ?? []);
    });
  }, [criteria, triggerCallGetNotifications]);

  const resetFilters = useCallback(
    (category = NotificationFamilyEnum.LAD) => {
      setFilters(getDefaultFilters(category));
      toggleDisplayedFilterOptionCategory(category);
      setIsDirty(false);
      setOpenedReporting(openedReporting && category === NotificationFamilyEnum.LAD);
      setPage(0);
      setSearchFilterFieldState({ type: 'RESET' });
    },
    [getDefaultFilters, openedReporting, toggleDisplayedFilterOptionCategory],
  );

  const updateFilters = useCallback((newFilters: Partial<NotificationSearchFilters>) => {
    setFilters(newFilters);
    setIsDirty(true);
  }, []);

  const values = useMemo(
    () => ({
      isDirty,
      notificationsTotal,
      criteria,
      filters,
      setFilters: updateFilters,
      resetFilters,
      displayedFilterOptions,
      ...restDisplaySearchFilterOption,
      selectedNotification,
      setSelectedNotification,
      notifications,
      isFetching,
      page,
      setPage,
      isOpenedExportDialog,
      setIsOpenedExportDialog,
      openedReporting,
      setOpenedReporting,
      droneStatistics,
      isFetchingDroneStatistics,
      openedFilterOptions,
      setOpenedFilterOptions,
      activeSearchFilterField,
      setActiveSearchFilterField,
      searchFilterFieldState,
      setSearchFilterFieldState,
      activeTab,
      setActiveTab,
    }),
    [
      isDirty,
      notificationsTotal,
      criteria,
      filters,
      updateFilters,
      resetFilters,
      displayedFilterOptions,
      restDisplaySearchFilterOption,
      selectedNotification,
      notifications,
      isFetching,
      page,
      isOpenedExportDialog,
      openedReporting,
      droneStatistics,
      isFetchingDroneStatistics,
      openedFilterOptions,
      activeSearchFilterField,
      searchFilterFieldState,
      activeTab,
    ],
  );

  return <NotificationSearchContext value={values}>{children}</NotificationSearchContext>;
};

export const useNotificationSearchContext = () => {
  const context = useContext(NotificationSearchContext);
  if (!context) {
    throw new Error('useNotificationSearchContext must be used within a NotificationSearchContextProvider');
  }
  return context;
};
